import { Session } from "@supabase/supabase-js";
import { create } from "zustand";
import { Database } from "../database.types";
import { supabaseClient } from "../shared/supabase";

type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type MemberRow = Database["public"]["Tables"]["members"]["Row"];
type SubscriptionRow = Database["public"]["Tables"]["subscriptions"]["Row"];
type CourseSubscriptionRow =
  Database["public"]["Tables"]["course_subscriptions"]["Row"];
type MemberInsert = Database["public"]["Tables"]["members"]["Insert"];
type MemberUpdate = Database["public"]["Tables"]["members"]["Update"];

export type Member = MemberRow & {
  subscriptions: SubscriptionRow[];
  course_subscriptions: CourseSubscriptionRow[];
};

export type Profile = ProfileRow & {
  members: Member[];
};

export type Subscription = SubscriptionRow;
export type CourseSubscription = CourseSubscriptionRow;
export type MemberType = "adult" | "junior" | "baby";

type UserState = {
  profile: Profile | null;
  isLoading: boolean;
  error: Error | null;
};

type UserStore = {
  initialize: () => Promise<void>;
  loadProfile: () => Promise<void>;
  addMember: (data: MemberInsert) => Promise<void>;
  updateMember: (data: MemberUpdate & { id: string }) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;
} & UserState;

const profileQuery = (user_id: string) =>
  supabaseClient
    .from("profiles")
    .select(
      `*,
    members(*,
      subscriptions(*),
      course_subscriptions!course_subscriptions_subscriber_id_fkey(*)
    )
  `
    )
    .eq("id", user_id)
    .single();

export const useProfileOwner = () => {
  return useUserStore((state) =>
    state.profile?.members.find((m) => m.is_owner)
  );
};

export const useUserStore = create<UserStore>((set, get) => {
  let _session: Session | null = null;

  return {
    profile: null,
    isLoading: false,
    error: null,

    initialize: async () => {
      // Listen to auth stat changes
      supabaseClient.auth.onAuthStateChange(async (event, session) => {
        if (event === "INITIAL_SESSION") {
          _session = session;
          get().loadProfile();
        }

        if (event === "SIGNED_IN") {
          if (session?.user.id !== _session?.user.id) {
            _session = session;
            get().loadProfile();
          }
        }

        if (event === "SIGNED_OUT") {
          _session = null;
          set({ profile: null });
        }

        if (event === "USER_UPDATED") {
          _session = session;
          get().loadProfile();
        }
      });
    },

    loadProfile: async () => {
      if (!_session) return;

      set({ isLoading: true, error: null });

      const userId = _session?.user.id;

      if (!userId) {
        console.error("No user id found in session", _session);
        return;
      }

      const { data, error } = await profileQuery(userId);

      if (error) {
        set({ error, isLoading: false });
        return;
      }

      if (!data) {
        return;
      }

      set({ profile: data, isLoading: false });
    },

    addMember: async (data: MemberInsert) => {
      const { error } = await supabaseClient
        .from("members")
        .insert(data)
        .select();
      if (error) {
        console.error("Error creating member:", error);
        return;
      }

      get().loadProfile();
    },

    updateMember: async (data: MemberUpdate & { id: string }) => {
      const { error } = await supabaseClient
        .from("members")
        .update(data)
        .eq("id", data.id)
        .select();
      if (error) {
        console.error("Error updating member:", error);
        return;
      }

      get().loadProfile();
    },

    deleteMember: async (id: string) => {
      const { error } = await supabaseClient
        .from("members")
        .delete()
        .eq("id", id);
      if (error) {
        console.error("Error deleting member:", error);
        return;
      }

      get().loadProfile();
    },
  };
});
